import { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { callLSQLapp, encryptBody, getLsqLeadBody } from "../services/lsq.js";

import {
  ctaClickEvent,
  formAbandonmentEvent,
  formFieldErrorEvent,
  formIntermediateEvent,
  formStartEvent,
  formSubmissionFailEvent,
  formSuccessEvent,
} from "../services/adobeDataLayer.js";

import queryString from "query-string";
import { useCookies } from "react-cookie";

function FormComponent() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    // defaultValues: {
    //   selectCustomerType: ["Individual"], // default selected
    // },
  });

  const mobileValue = watch("mobile");
  const panValue = watch("pan");

  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [timer, setTimer] = useState(60);
  const [customer, setCustomer] = useState("Individual");
  const [disableApplyNow, setDisableApplyNow] = useState(false);

  // States for PAN GST verification
  const [panGstVerified, setPanGstVerified] = useState(false);
  const [panGstError, setPanGstError] = useState("");
  const [panGstLoading, setPanGstLoading] = useState(false);

  //States for Pincode and City
  const [cityName, setCityName] = useState("");
  const [citydefined, setCitydefined] = useState(false);
  const [solID, setSolID] = useState("");
  const [branch, setBranch] = useState("");
  const [options, setOptions] = useState([]);

  //Store lead create payload values
  const formValuesRef = useRef({});

  //Store Fbp and Fbc from cookies
  const [cookies, setCookie] = useCookies();
  formValuesRef.current.FBClickID = cookies["_fbp"] || "";
  formValuesRef.current.FBPixelID = cookies["_fbc"] || "";

  const hasTriggeredFormStart = useRef(false);
  let lastAccessedField = "na";

  const showToast = (type, message) => {
    const toast = document.createElement("div");
    toast.className = `toast ${type}`;
    toast.textContent = message;

    document.getElementById("toastContainer").appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  // Adobe Event for form abandonment
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      //console.log("lead form started: ", hasTriggeredFormStart.current)
      // Custom logic to be executed before tab or window is closed
      if (hasTriggeredFormStart.current) {
        formAbandonmentEvent(
          "CA_Customer initiated lead form",
          "lead generation",
          lastAccessedField,
          "details",
        );
        //console.table(window.adobeDataLayer)
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    //Clean Up
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  /* PAN GST Verification */
  useEffect(() => {
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    if (customer === "Individual" || !panPattern.test(panValue)) {
      setPanGstVerified(false);
      setPanGstError("");
      return;
    }

    const verifyPanGst = async () => {
      setPanGstLoading(true);
      setPanGstError("");
      try {
        console.log("Verifying PAN with GST");
        const res = await callLSQLapp(
          { PAN: panValue },
          "SIGNZY_PAN_TO_GSTIN_LIST",
          { firstName: getValues("fullName"), entityType: customer },
        );

        const rawMessage = res?.data?.message;

        // Check for upstream server error — allow user to proceed and log it
        if (typeof rawMessage === "string") {
          try {
            const parsed = JSON.parse(rawMessage);
            if (parsed?.error?.statusCode === 409) {
              const { statusCode, message: errMsg } = parsed.error;
              formValuesRef.current.Signzy_GSTINLIST_Server_Error = `${statusCode} - ${errMsg}`;
              console.log(
                "PAN GST upstream error — allowing proceed:",
                parsed.error,
              );
              setPanGstVerified(true);
              return;
            }
          } catch (_) {
            // rawMessage is a plain string, not JSON — fall through
          }
        }

        const gstinList = rawMessage?.result?.gstinList;
        // console.log("Verifying PAN with GST Resp: ", gstinList);
        if (Array.isArray(gstinList) && gstinList.length > 0) {
          setPanGstVerified(true);
          console.log("PAN with GST Verified");
        } else {
          console.log("PAN with GST Not Verified");
          setPanGstVerified(false);
          setPanGstError(
            "Enter GST linked PAN number of your registered business",
          );
        }
      } catch (err) {
        setPanGstVerified(false);
        setPanGstError(
          "Enter GST linked PAN number of your registered business",
        );
        console.log("PAN with GST Not Verified");
      } finally {
        setPanGstLoading(false);
      }
    };

    verifyPanGst();
  }, [panValue, customer]);

  /* OTP Timer */
  useEffect(() => {
    if (!otpSent || otpVerified || timer === 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [otpSent, otpVerified, timer]);

  const handleSendOtp = async () => {
    ctaClickEvent(`otp:${otpSent}`, "lead form", "button", 0, "na");
    if (!mobileValue) return;
    if (mobileValue.length === 10) {
      try {
        let payload = { mobile: mobileValue, messageType: "otpGeneral" };
        let param = { otp: "send" };
        const res = await callLSQLapp(payload, "OTP", param);
        if (res.data.message.response.sent) {
          formIntermediateEvent("receive", "otp");
          showToast("success", "An OTP has been sent to your Mobile number.");
          setOtpVerified(false);
          setTimer(60);
          setOtpSent(true);
        }
      } catch (error) {
        showToast("error", "Error occurred while generating OTP.");
      }
    }
  };

  const handleVerifyOtp = async () => {
    ctaClickEvent("otp:verify", "lead form", "button", 0, "na");
    if (!mobileValue) {
      showToast("error", "Please enter mobile number");
      return;
    }
    if (otp.length === 6) {
      try {
        let payload = { mobile: mobileValue, otp: otp };
        let param = { otp: "verify" };
        const res = await callLSQLapp(payload, "OTP", param);
        if (res.data.message === "Failure Occured, please check logs") {
          showToast("error", "Please enter valid OTP");
        } else {
          formIntermediateEvent("verified", "otp");
          setOtpVerified(true);
          setTimer(0);
          showToast("success", "OTP verified.");

          formValuesRef.current.token =
            res.data.message.response.dataSent.UniqueID;
        }
      } catch (error) {
        showToast("error", "Error verifying OTP.");
      }
    }
  };

  const handlePincode = async (e) => {
    const pin = e.target.value;

    if (pin?.length !== 6) {
      // reset when not 6 digits yet
      setCityName("");
      setCitydefined(false);
      setOptions([]);
      setSolID("");
      return;
    }

    try {
      setCityName("");
      const payload = { pincode: pin };
      const param = { type: "CurrentAccountNCA" };

      const response = await callLSQLapp(payload, "MAVIS", param);
      console.log("Response from Mavis: ", response);

      // 👉 Adjusted to your actual API shape:
      const { Data = [], Count = 0 } = response?.data?.message ?? {};

      if (Count > 0 && Array.isArray(Data)) {
        // Update city name from first record (if present)
        const first = Data[0];
        const city = first?.City || "";
        setCityName(city);
        setCitydefined(Boolean(city));
        // Populate branches
        setOptions(Data);
        // Clear previous selection
        setSolID("");
      } else {
        // No branches found
        setCityName("City not found");
        setCitydefined(false);
        setOptions([]);
        setSolID("");
      }
    } catch (err) {
      console.error("Error fetching branches:", err);
      setCityName("City not found");
      setCitydefined(false);
      setOptions([]);
      setSolID("");
    }
  };

  const handleBranch = (e) => {
    const solID = e.target.value; // Sol_ID
    setSolID(solID);

    // If you want branch name:
    const selectedBranch = options.find((o) => o.Sol_ID === solID);

    if (selectedBranch) {
      const branchName = selectedBranch.Branch_Name;
      setBranch(branchName);
      console.log("Branch Name:", branchName);
      console.log("Sol ID:", solID);
    }
  };

  const handleFormStart = () => {
    if (!hasTriggeredFormStart.current) {
      formStartEvent(
        "CA_Customer initiated lead form",
        "lead generation",
        "details",
      );
      hasTriggeredFormStart.current = true;
    }
  };

  const onError = (errors) => {
    const simpleErrors = Object.fromEntries(
      Object.entries(errors).map(([key, value]) => [key, value.message]),
    );
    formFieldErrorEvent(simpleErrors);
    console.log("Simplified errors:", simpleErrors);
  };

  const handleReset = () => {
    reset();
    setOtp("");
    setOtpSent(false);
    setOtpVerified(false);
    setTimer(60);
  };

  const onSubmit = async (data) => {
    if (!otpSent && mobileValue.length === 10) {
      showToast("error", "Click on Send OTP to continue.");
      return;
    } else if (!otp) {
      showToast("error", "Please enter OTP to continue.");
      return;
    } else if (!otpVerified) {
      showToast("error", "Please verify OTP to continue.");
      return;
    }

    if (customer !== "Individual" && !panGstVerified) {
      showToast(
        "error",
        "Enter GST linked PAN number of your registered business",
      );
      return;
    }

    if (!citydefined) {
      showToast("error", "Please enter valid pincode.");
      return;
    }
    setDisableApplyNow(true);

    formValuesRef.current.fullname = data.fullName;
    formValuesRef.current.entityType = data.selectCustomerType;
    formValuesRef.current.entityName = data.nameOfEntity;
    formValuesRef.current.mobile = data.mobile;
    formValuesRef.current.pan = data.pan;
    formValuesRef.current.pincode = data.pincode;
    formValuesRef.current.CityName = cityName;
    formValuesRef.current.solID = solID;
    formValuesRef.current.branch = branch;

    const utmParamsObj = queryString.parse(window.location.search);
    for (const [key, value] of Object.entries(utmParamsObj)) {
      if (key !== "campaign_code") {
        formValuesRef.current[key] = value;
      }
    }

    formValuesRef.current.CampaignId = "2765190";
    formValuesRef.current.ApiProductName = "CA_Customer initiated";

    formValuesRef.current.ParentProduct = "Current Account";
    formValuesRef.current.ModeOfLead = "landing-page";

    if (data.agreetnc === true) {
      formValuesRef.current.DND = "Yes";
      formValuesRef.current.terms_chk = true;
    } else {
      formValuesRef.current.DND = "No";
      formValuesRef.current.terms_chk = false;
    }

    formValuesRef.current.IsEligible = "Yes";
    formValuesRef.current.EligibilityReason = "";
    formValuesRef.current.CompleteUrl = window.location.href;
    formValuesRef.current.User_Agent = navigator.userAgent;

    console.log("Final Submitted Data:", formValuesRef);

    let payload = {
      mobile: data.mobile,
      otp: otp,
      formdata: getLsqLeadBody(formValuesRef.current),
    };
    console.log("Lead Create Payload", payload);

    //Encrypt Payload
    const encryptedPayload = await encryptBody(payload);
    // console.log("Lead Create encrypted Payload", encryptedPayload);

    let param = { product: "CA_Customer initiated" };
    let createLeadResp = await callLSQLapp(encryptedPayload, "CREATE", param);
    if (createLeadResp.data.message !== "Failure occured , please check logs") {
      formSuccessEvent(
        "CA_Customer initiated lead form",
        { otp: otp, pincode: data.pincode, city: cityName },
        "lead generation",
        "thank you",
        true,
      );
      let leadid = createLeadResp.data.message.Data.LeadApiCalls.Message.Id;
      console.log("leadID", leadid);
      navigate(`/thankyou?id=${leadid}`);
    } else {
      setDisableApplyNow(false);
      formSubmissionFailEvent();
      console.log("lead not created.");
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      onChange={handleFormStart}
      id="myForm"
    >
      <h3>Fill the form below</h3>

      <div className="form_wrap">
        {/* Full Name */}
        <div className="input_wrapper">
          <input
            type="text"
            id="name"
            placeholder="Your Name"
            // disabled={otpVerified}
            {...register("fullName", {
              required: "Please enter your name",
              pattern: {
                value: /^[a-zA-Z\s]+$/,
                message: "Only alphabets and spaces allowed",
              },
            })}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
            }}
          />
          {errors.fullName && (
            <span className="error">{errors.fullName.message}</span>
          )}
        </div>

        {/* customer type */}
        <div className="input_wrapper">
          <label className="form-label" htmlFor="selectCustomerType">
            Select your Entity Type
          </label>
          <select
            id="selectCustomerType"
            {...register("selectCustomerType", {
              required: "Select Customer type is required",
            })}
            defaultValue="Individual"
            onChange={(e) => {
              setCustomer(e.target.value);
            }}
          >
            <option value="Individual">Individual</option>
            <option value="Proprietorship">Proprietorship</option>
            <option value="Partnership">Partnership</option>
            <option value="Private Ltd">Private Ltd</option>
            <option value="Public Ltd">Public Ltd</option>
            <option value="LLP">LLP</option>
            {/* <option value="Others">Others</option> */}
          </select>
          <img
            src="dist/img/down.svg"
            className="arrow"
            width={18}
            height={18}
            alt="arrow"
          ></img>
          {errors.selectCustomerType && (
            <span className="error">{errors.selectCustomerType.message}</span>
          )}
        </div>

        {/* Name of Entity */}
        <div className="input_wrapper">
          <input
            placeholder="Name of Entity"
            {...register("nameOfEntity", {
              required: "Please enter name of entity",
              pattern: {
                value: /^[a-zA-Z\s]+$/,
                message: "Only alphabets allowed",
              },
            })}
            onInput={(e) =>
              (e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, ""))
            }
          />
          {errors.nameOfEntity && (
            <span className="error">{errors.nameOfEntity.message}</span>
          )}
        </div>

        {/* Email */}
        {/* <div className="input_wrapper">
          <input
            placeholder="Email ID"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter valid email"
              }
            })}
          />
          {errors.email && <span className="error">{errors.email.message}</span>}
        </div> */}

        {/* Mobile */}
        <div className="otp_wrapper input_wrapper">
          <div className="d-flex gap-2">
            <input
              type="text"
              placeholder="Mobile Number"
              disabled={otpVerified}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              {...register("mobile", {
                required: "Mobile number is required",
                pattern: {
                  value: /^\d{10}$/,
                  message: "Enter valid 10-digit mobile number",
                },
              })}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
              }}
            />
            {!otpVerified && !otpSent && (
              <button
                type="button"
                className="resend btn_abs"
                onClick={handleSendOtp}
              >
                Send OTP
              </button>
            )}
            {otpSent && timer <= 0 && (
              <button
                type="button"
                className="resend btn_abs"
                onClick={handleSendOtp}
                disabled={otpVerified}
              >
                {otpVerified ? `Verified` : `Resend OTP`}
              </button>
            )}
          </div>
          {errors.mobile && (
            <span className="error">{errors.mobile.message}</span>
          )}
        </div>

        {/* OTP */}
        {otpSent && !otpVerified && timer > 0 && (
          <div className="otp_wrapper input_wrapper">
            <div className="d-flex align-items-center gap-2">
              <input
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                inputMode="numeric"
              />
              <span className="timer">
                00:{timer < 10 ? `0${timer}` : timer}
              </span>
              <button
                type="button"
                className="resend btn_abs1"
                onClick={handleVerifyOtp}
              >
                Verify OTP
              </button>
            </div>
            {errors.otp && <span className="error">{errors.otp.message}</span>}
          </div>
        )}

        {/* PAN */}
        <div className="input_wrapper">
          <input
            type="text"
            id="pan"
            placeholder={
              customer === "Individual" ? "PAN of Individual" : "PAN of Entity"
            }
            maxLength={10}
            disabled={panGstVerified}
            {...register("pan", {
              required: "PAN number is required",
              pattern: {
                value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                message: "Invalid PAN format",
              },
            })}
            onInput={(e) => {
              e.target.value = e.target.value.toUpperCase();
            }}
          />

          {/* {panGstLoading && <span className="info">Verifying PAN...</span>} */}
          {!panGstLoading && panGstError && (
            <span className="error">{panGstError}</span>
          )}
          {!panGstLoading && !panGstError && errors.pan && (
            <span className="error">{errors.pan.message}</span>
          )}
        </div>

        {/* Pincode */}
        <div className="input_wrapper">
          <input
            type="text"
            id="pincode"
            placeholder="Business PIN Code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            {...register("pincode", {
              required: "PIN code is required",
              pattern: {
                value: /^\d{6}$/,
                message: "Enter valid PIN code",
              },
            })}
            onInput={(e) =>
              (e.target.value = e.target.value.replace(/\D/g, ""))
            }
            onChange={handlePincode}
          />
          {errors.pincode && (
            <span className="error">{errors.pincode.message}</span>
          )}
        </div>

        {/* Branch */}
        {options.length > 0 && (
          <div className="input_wrapper">
            <select
              id="branch"
              className=""
              {...register("branch", {
                required: "Select Branch",
              })}
              onChange={handleBranch}
              defaultValue=""
            >
              <option value="" disabled>
                Select a branch
              </option>

              {options.map((option) => (
                <option key={option.Sol_ID} value={option.Sol_ID}>
                  {option.Branch_Name}
                </option>
              ))}
            </select>

            {errors.branch && (
              <span className="error">{errors.branch.message}</span>
            )}
          </div>
        )}

        {/* Terms */}
        <div className="terms input_wrapper d-flex gap-2 mt-3">
          <Controller
            name="agreetnc"
            control={control}
            rules={{ required: "Agree to Terms & Conditions" }}
            render={({ field }) => (
              <input
                type="checkbox"
                id="agreetnc"
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked);
                  ctaClickEvent(
                    `consent checkbox: ${e.target.checked}`,
                    "lead form",
                    "checkbox",
                    0,
                    "na",
                  );
                }}
              />
            )}
          />
          <label htmlFor="agreetnc">
            I authorise ICICI Bank and its representatives to contact me through
            phone, email, SMS and WhatsApp. I also agree to{" "}
            <a
              target="_blank"
              href="https://www.icici.bank.in/content/dam/icicibank/india/managed-assets/docs/terms-condition/current_ac_tc.pdf?ITM=nli__businessTermsConditions_na_15_na_na_NLI"
              onClick={() => {
                handleFormStart();
                ctaClickEvent(
                  "terms & conditions",
                  "lead form",
                  "text link",
                  0,
                  "https://www.icici.bank.in/content/dam/icicibank/india/managed-assets/docs/terms-condition/current_ac_tc.pdf?ITM=nli__businessTermsConditions_na_15_na_na_NLI",
                );
              }}
              onContextMenu={() => {
                handleFormStart();
                ctaClickEvent(
                  "terms & conditions",
                  "lead form",
                  "text link",
                  0,
                  "https://www.icici.bank.in/content/dam/icicibank/india/managed-assets/docs/terms-condition/current_ac_tc.pdf?ITM=nli__businessTermsConditions_na_15_na_na_NLI",
                );
              }}
            >
              ICICI Bank’s Terms & Conditions.
            </a>
          </label>
        </div>
        {errors.agreetnc && (
          <span className="error">{errors.agreetnc.message}</span>
        )}

        {/* <button type="button" className="reset" onClick={handleReset}>
          Reset Details
        </button> */}

        <button type="submit" className="proceed" disabled={disableApplyNow}>
          Apply Now
        </button>

        <div id="toastContainer" className="toast-container" />
      </div>
    </form>
  );
}

export default FormComponent;
